import { unified } from "unified";
import remarkParse from "remark-parse";
import type { Root, Heading, ListItem, Text, Paragraph } from "mdast";
import { Feature, SpecMetadata, ParsedSpec, Complexity, Priority } from "./schemas.js";

// ── Helpers ─────────────────────────────────────────────────

function getTextContent(node: { children?: unknown[] } | undefined): string {
  if (!node || !node.children) return "";
  return (node.children as Array<{ value?: string; children?: unknown[] }>)
    .map((child) => child.value ?? getTextContent(child as { children?: unknown[] }))
    .join("")
    .trim();
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

// ── Frontmatter parser ──────────────────────────────────────

function parseFrontmatter(content: string): { metadata: SpecMetadata; body: string } {
  const match = content.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) return { metadata: {}, body: content };

  const frontmatter = match[1];
  const body = match[2];
  const metadata: Record<string, unknown> = {};

  for (const line of frontmatter.split("\n")) {
    const kv = line.match(/^(\w+):\s*"?([^"]*)"?\s*$/);
    if (kv) {
      const key = kv[1];
      const val = kv[2];
      metadata[key] = key === "team_size" ? parseInt(val, 10) : val;
    }
  }

  return { metadata: SpecMetadata.parse(metadata), body };
}

// ── Feature metadata extractor ──────────────────────────────

interface RawFeatureMeta {
  category?: string;
  priority?: string;
  complexity?: string;
  description?: string;
  dependencies?: string;
  aiTasks?: string;
  acceptanceCriteria?: string;
}

function parseFeatureList(nodes: Array<{ children?: unknown[] }>): RawFeatureMeta {
  const meta: RawFeatureMeta = {};

  for (const node of nodes) {
    const text = getTextContent(node as { children?: unknown[] });
    const match = text.match(/^\*?\*?(\w[\w\s]*?)\*?\*?:\s*(.+)$/);
    if (!match) continue;

    const key = match[1].trim().toLowerCase();
    const value = match[2].trim();

    switch (key) {
      case "category":
        meta.category = value;
        break;
      case "priority":
        meta.priority = value.replace(/\s*\(.*\)/, "");
        break;
      case "complexity":
        meta.complexity = value;
        break;
      case "description":
        meta.description = value;
        break;
      case "dependencies":
        meta.dependencies = value;
        break;
      case "ai tasks":
        meta.aiTasks = value;
        break;
      case "acceptance criteria":
        meta.acceptanceCriteria = value;
        break;
    }
  }

  return meta;
}

// ── Main parser ─────────────────────────────────────────────

export function parseSpec(content: string): ParsedSpec {
  const { metadata, body } = parseFrontmatter(content);

  const tree = unified().use(remarkParse).parse(body) as Root;
  const features: Feature[] = [];

  let i = 0;
  const nodes = tree.children;

  while (i < nodes.length) {
    const node = nodes[i];

    // Look for ### Feature: <name> headings
    if (node.type === "heading" && (node as Heading).depth === 3) {
      const headingText = getTextContent(node as { children?: unknown[] });
      const featureMatch = headingText.match(/^Feature:\s*(.+)$/i);

      if (featureMatch) {
        const name = featureMatch[1].trim();
        i++;

        // Collect list items following the heading
        const listNodes: Array<{ children?: unknown[] }> = [];
        while (i < nodes.length && nodes[i].type === "list") {
          const list = nodes[i] as { children: Array<{ children?: unknown[] }> };
          listNodes.push(...list.children);
          i++;
        }

        const meta = parseFeatureList(listNodes);
        const complexity = isValidComplexity(meta.complexity) ? meta.complexity : "M";
        const priority = isValidPriority(meta.priority) ? meta.priority : undefined;

        features.push({
          id: slugify(name),
          name,
          category: meta.category ?? "general",
          priority,
          complexity,
          description: meta.description ?? name,
          dependencies: meta.dependencies
            ? meta.dependencies.split(",").map((d) => d.trim())
            : [],
          aiTasks: meta.aiTasks ? meta.aiTasks.split(",").map((t) => t.trim()) : [],
          acceptanceCriteria: meta.acceptanceCriteria,
        });

        continue;
      }
    }

    i++;
  }

  return { metadata, features, rawContent: content };
}

function isValidComplexity(val: string | undefined): val is Complexity {
  return val !== undefined && ["S", "M", "L", "XL"].includes(val);
}

function isValidPriority(val: string | undefined): val is Priority {
  return val !== undefined && ["P0", "P1", "P2", "P3"].includes(val);
}
